import { hookstate, useHookstate } from '@hookstate/core';
import {
  InsuranceCategory,
  InsuranceType,
  InsuranceFormDataType,
  InsuranceCalculateRequest,
} from '../types/insurance.types';

// Constants for localStorage keys
const FORM_DATA_STORAGE_KEY = 'insurance_form_data';

// Function to get initial ID from URL
const getInitialIdFromUrl = (): string | undefined => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('id') || undefined;
  }
  return undefined;
};

// Function to save form data to localStorage
const saveFormDataToLocalStorage = (formData: any) => {
  try {
    // Create a new object without departure and return dates
    const { departureDate, returnDate, ...dataToSave } = formData;

    // Add timestamp
    const finalData = {
      ...dataToSave,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(FORM_DATA_STORAGE_KEY, JSON.stringify(finalData));
    return true;
  } catch (error) {
    console.error('Error saving form data to localStorage:', error);
    return false;
  }
};

// Function to load form data from localStorage
const loadFormDataFromLocalStorage = () => {
  try {
    const savedDataString = localStorage.getItem(FORM_DATA_STORAGE_KEY);
    if (!savedDataString) return null;

    const savedData = JSON.parse(savedDataString);
    return savedData;
  } catch (error) {
    console.error('Error loading form data from localStorage:', error);
    return null;
  }
};

// Function to clear saved form data from localStorage
const clearSavedFormData = () => {
  try {
    localStorage.removeItem(FORM_DATA_STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing form data from localStorage:', error);
    return false;
  }
};

// Function to check if form data exists in localStorage for a specific insurance
const hasSavedFormData = () => {
  try {
    const savedDataString = localStorage.getItem(FORM_DATA_STORAGE_KEY);
    return !!savedDataString;
  } catch (error) {
    console.error('Error checking for saved form data:', error);
    return false;
  }
};

// Create centralized insurance state
const globalState = hookstate({
  topbar: {
    title: null as string | null,
  },
  isError: false as boolean,
  errorMessage: null as string | null,
  loading: true as boolean,
  selectedCategory: undefined as InsuranceCategory | undefined,
  id: getInitialIdFromUrl() as string | undefined,
  formStep: 1,
  direction: 0 as 0 | 1, // 0 for forward, 1 for backward
  searchForm: {} as InsuranceCalculateRequest,
  form: {} as InsuranceFormDataType,
  insurancePlans: [] as Array<InsuranceType>,
  quoteId: null as string | null,
});

// Function to update URL when ID changes
const updateUrlWithId = (id: string | undefined) => {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (id) {
      url.searchParams.set('id', id);
    } else {
      url.searchParams.delete('id');
    }
    window.history.pushState({}, '', url);
  }
};

// Export hook for components to use the state with direct access
export const useInsuranceState = () => {
  // This creates a subscription to the state that will trigger re-renders
  const state = useHookstate(globalState);

  // Create a custom setter function for the ID that also updates the URL
  const setId = (value: string | undefined) => {
    state.id.set(value);
    updateUrlWithId(value);
  };

  // Function to save current form state to localStorage
  const saveFormData = () => {
    console.log('Saving form data to localStorage');
    const formData = state.form.get();
    return saveFormDataToLocalStorage(formData);
  };

  // Function to load saved form data from localStorage
  const loadFormData = () => {
    const savedData = loadFormDataFromLocalStorage();
    if (!savedData) return false;

    // Update form state with saved data
    // Handle date objects which need to be converted from strings
    const formData = { ...savedData };

    // We don't load departureDate and returnDate as they're not saved

    if (formData.dateOfBirth) {
      state.form.dateOfBirth.set(formData.dateOfBirth);
    }

    // Handle insured persons date of birth conversion
    if (formData.insuredPersons && Array.isArray(formData.insuredPersons)) {
      const updatedInsuredPersons = formData.insuredPersons.map(
        (person: any) => ({
          ...person,
          dateOfBirth: person.dateOfBirth ? person.dateOfBirth : null,
        })
      );
      state.form.insuredPersons.set(updatedInsuredPersons);
    }

    // Update other form fields
    Object.keys(formData).forEach((key) => {
      if (
        key !== 'timestamp' &&
        key !== 'dateOfBirth' &&
        key !== 'insuredPersons'
      ) {
        // Check if the key exists in the form state
        if (key in state.form) {
          (state.form as any)[key].set(formData[key]);
        }
      }
    });

    return true;
  };

  // Function to check if there's saved form data
  const checkForSavedFormData = () => {
    return hasSavedFormData();
  };

  // Function to clear saved form data
  const clearFormData = () => {
    return clearSavedFormData();
  };

  return {
    get: () => state.get(),
    isError: state.isError,
    errorMessage: state.errorMessage,
    loading: state.loading,
    topbar: state.topbar,
    selectedCategory: state.selectedCategory,
    id: {
      get: () => state.id.get(),
      set: setId,
    },
    searchForm: state.searchForm,
    form: state.form,
    formStep: state.formStep,
    direction: state.direction,
    insurancePlans: state.insurancePlans,
    quoteId: state.quoteId,
    localStorage: {
      saveFormData,
      loadFormData,
      checkForSavedFormData,
      clearFormData,
    },
  };
};
